import { useState, useEffect } from "react";
import { Card, Form, Row, Col, NavDropdown, Modal } from "react-bootstrap";
import "../assets/css/toggleMode.css";
import { useTranslate } from "../hooks/LanguageProvider";
import TimezoneSelect, { ITimezone } from "react-timezone-select";
import { PropagateLoader } from "react-spinners";
import { Bounce, toast } from "react-toastify";
import { useUser } from "../context/UserContext";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";

type LanguageKey = "en" | "fr" | "ar" | "es";

interface UserInfo {
  id_user?: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  img?: string;
  img_exists?: boolean;
  timezone?: string;
  language?: LanguageKey;
  theme_mode?: string | number;
}

function Profile() {
  const [showModalImg, setShowModalImg] = useState(false);
  const id_user = localStorage.getItem("userid");
  const [pathImg, setPathImg] = useState<string | null>(null);
  const [emailValue, setEmailValue] = useState<string>("");
  const [usernameValue, setUsernameValue] = useState<string>("");
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [refreshData, setRefreshData] = useState(false);
  const [loading, setLoading] = useState(false);
  const { translate, lang, setLang } = useTranslate();
  const [darkMode, setDarkMode] = useState("0");
  const { refreshImage } = useUser();
  const [selectedTimezone, setSelectedTimezone] = useState<ITimezone>(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  const strLang: Record<LanguageKey, string> = {
    en: "English",
    fr: "French",
    ar: "Arabic",
    es: "Spanish",
  };

  const fetchInfo = async () => {
    if (!id_user) return;

    setLoading(true);

    try {
      const response = await fetch(`${backendUrl}/api/profile/${id_user}`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const data = await response.json();

      if (!data || !data[0]) {
        throw new Error("No profile data returned");
      }

      const user: UserInfo = data[0];

      if (user.img) {
        if (user.img.startsWith("asset/")) {
          setPathImg(`${backendUrl}/${user.img}?t=${Date.now()}`);
        } else {
          setPathImg(`${backendUrl}/asset/images/logo.png?t=${Date.now()}`);
        }
      } else {
        setPathImg(`${backendUrl}/asset/images/logo.png?t=${Date.now()}`);
      }

      setEmailValue(user.email?.toString() || "");
      setUsernameValue(user.username?.toString() || "");
      setSelectedTimezone(
        user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      );
      setDarkMode(String(user.theme_mode) === "1" ? "1" : "0");
      setLang((user.language as LanguageKey) || "fr");
      setUserInfo(user);
    } catch (err) {
      console.error("fetchInfo error:", err);
      toast.warn(translate("Error loading profile"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInfo();
  }, []);

  useEffect(() => {
    if (refreshData) {
      fetchInfo();
      setRefreshData(false);
    }
  }, [refreshData]);

  const refreshDataFunc = () => {
    setRefreshData(true);
  };

  const handleLanguageChange = async (newLang: LanguageKey) => {
    try {
      setLang(newLang);

      toast.success(translate("Language changed successfully"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
    } catch (error) {
      toast.warn(translate("Error changing language"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
    }
  };

  const [isDarkTheme, setIsDarkTheme] = useState<boolean>(false);

  const handleThemeChange = async (themeMode: boolean) => {
    setIsDarkTheme(!isDarkTheme);

    if (isDarkTheme) {
      document.body.classList.remove("dark-theme");
      document.body.classList.add("light-theme");
    } else {
      document.body.classList.remove("light-theme");
      document.body.classList.add("dark-theme");
    }

    try {
      const response = await fetch(
        `${backendUrl}/api/profile/${id_user}/update-dark-mode`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            theme_mode: themeMode ? "1" : "0",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Erreur HTTP! Status: ${response.status}`);
      }

      setDarkMode(themeMode ? "1" : "0");

      toast.success(translate("Theme changed successfully"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
    } catch (error) {
      toast.warn(translate("Error changing theme"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
    }
  };

  const handelTimeZone = async (timezone: any) => {
    try {
      const response = await fetch(
        `${backendUrl}/api/profile/${id_user}/update-timezone`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            timezone: timezone.value || timezone,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Erreur HTTP! Status: ${response.status}`);
      }

      setSelectedTimezone(timezone);

      toast.success(translate("Timezone changed successfully"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
    } catch (error) {
      toast.warn(translate("Error changing timezone"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const maxSizeInBytes = 2 * 1024 * 1024;

    if (file && file.size > maxSizeInBytes) {
      toast.warn(
        translate(
          "Le fichier dépasse la taille maximale autorisée (2 Mo). Veuillez sélectionner un fichier plus petit."
        ),
        {
          position: "bottom-right",
          autoClose: 2400,
          transition: Bounce,
        }
      );
      return;
    }

    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const dataURL = reader.result;
        saveImage(id_user ?? "", dataURL as string);
      };
      reader.readAsDataURL(file);

      toast.success(translate("Image updated successfully"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
    }
  };

  const saveImage = async (id_user: string, dataURL: string) => {
    try {
      const response = await fetch(
        `${backendUrl}/api/profile/${id_user}/upload-image`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ dataURL }),
        }
      );

      if (!response.ok) throw new Error("Échec de l'upload");

      await refreshImage();
      refreshDataFunc();
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error(translate("Image update failed"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
    }
  };

  const deleteImage = async (id_user: string) => {
    try {
      const response = await fetch(
        `${backendUrl}/api/profile/${id_user}/delete-image`,
        {
          method: "POST",
        }
      );

      if (response.ok) {
        setPathImg(`${backendUrl}/asset/images/logo.png?t=${Date.now()}`);
        await refreshImage();
        refreshDataFunc();

        toast.success(translate("Image deleted successfully"), {
          position: "bottom-right",
          autoClose: 2400,
          transition: Bounce,
        });
      } else {
        toast.warn(translate("Error deleting image"), {
          position: "bottom-right",
          autoClose: 2400,
          transition: Bounce,
        });
      }
    } catch (error) {
      console.error("Error deleting image", error);
    }
  };

  return (
    <div>
      <h1 style={{ color: "gray", marginTop: "10px" }}>
        {translate("Profile Settings")}
      </h1>

      {loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <PropagateLoader color={"#123abc"} loading={loading} size={20} />
        </div>
      )}

      {userInfo && !loading && (
        <div
          className="d-flex flex-column justify-content-between rounded-lg"
          style={{ border: "0.5px solid #ddd", padding: "20px" }}
        >
          <Row>
            <Col>
              <h3>{translate("Profile picture")}</h3>
              <p style={{ color: "gray" }}>
                {translate("Formats png, jpg. Maximum size: 2 MB.")}
              </p>
              <Row className="align-items-center">
                <Col xs={10} lg={2}>
                  {pathImg && (
                    <img
                      className="img-thumbnail"
                      src={pathImg}
                      alt="profile"
                      style={{ width: "250px" }}
                    />
                  )}
                </Col>
                <Col
                  xs={10}
                  lg={10}
                  className="d-flex flex-column flex-lg-row align-items-center"
                >
                  <input
                    id="fileInput"
                    type="file"
                    accept="image/png, image/jpeg"
                    style={{ display: "none" }}
                    onChange={handleImageUpload}
                  />
                  <label htmlFor="fileInput" className="btn btn-primary mr-2">
                    {translate("Choose a photo")}
                  </label>
                  <label
                    className="btn btn-outline-danger"
                    onClick={() => id_user && deleteImage(id_user)}
                  >
                    {translate("Delete photo")}
                  </label>
                </Col>
              </Row>
            </Col>
          </Row>

          <hr className="w-100" style={{ borderTop: "2px solid gray" }} />

          <Row className="mb-3">
            <Col>
              <h3 className="font-weight-normal">
                {translate("Personal info")}
              </h3>

              <Row style={{ display: "flex" }}>
                <Card body style={{ color: "gray", border: "0" }}>
                  {translate(
                    "When a user wants to change their username or email address, they must send a confirmation email to validate the change."
                  )}
                </Card>

                <form
                  className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center my-auto"
                  style={{ flex: "1 0 0%", marginLeft: "10px" }}
                >
                  <Form.Group className="mr-3">
                    <Form.Label>{translate("Username")}</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Username"
                      value={usernameValue}
                      onChange={(e) => setUsernameValue(e.target.value)}
                      disabled
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>{translate("Email address")}</Form.Label>
                    <div className="input-group">
                      <Form.Control
                        type="email"
                        placeholder="e-mail.geotrackin.com"
                        value={emailValue}
                        onChange={(e) => setEmailValue(e.target.value)}
                        disabled
                      />
                    </div>
                  </Form.Group>
                </form>
              </Row>
            </Col>
          </Row>

          <hr className="w-100" style={{ borderTop: "2px solid gray" }} />

          <Row className="mb-3" xs={12}>
            <Col
              xs={12}
              lg={10}
              className="d-flex flex-column flex-lg-row align-items-center"
            >
              <h3 className="font-weight-normal">{translate("Time Zone")}</h3>
              <div className="my-4 mx-2 select-wrapper">
                <TimezoneSelect
                  labelStyle="abbrev"
                  value={selectedTimezone}
                  onChange={(e) => handelTimeZone(e)}
                />
              </div>
              <button
                className="btn btn-outline-info"
                onClick={() =>
                  handelTimeZone(
                    Intl.DateTimeFormat().resolvedOptions().timeZone
                  )
                }
              >
                {translate("Reset")}
              </button>
            </Col>
          </Row>

          <hr className="w-100" style={{ borderTop: "2px solid gray" }} />

          <Row className="mb-3" xs={12}>
            <Col xs={12} md={4}>
              <div style={{ marginBottom: "10px" }}>
                <h3 className="font-weight-normal">{translate("Language")}</h3>
                <NavDropdown
                  title={
                    <span>
                      <img
                        src={`asset/images/small/flag-${lang}.png`}
                        alt={lang}
                      />
                      <span> {strLang[lang as LanguageKey]}</span>
                    </span>
                  }
                  id="navbarScrollingDropdown"
                  style={{
                    width: "180px",
                    border: "1px solid #ccc",
                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  {(Object.keys(strLang) as LanguageKey[]).map((key) => (
                    <NavDropdown.Item
                      key={key}
                      onClick={() => handleLanguageChange(key)}
                    >
                      <img
                        src={`asset/images/small/flag-${key}.png`}
                        alt={`img-flag-${key}`}
                        className="img-fluid image-flag mr-2"
                      />
                      {strLang[key]}
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>
              </div>
            </Col>

            <Col xs={12} md={6} className="mb-3">
              <div>
                <h3 className="font-weight-normal">
                  {darkMode === "1"
                    ? translate("Dark theme")
                    : translate("Light theme")}
                </h3>

                <div className="mt-3">
                  <Form.Switch
                    id="darkmode-toggle"
                    checked={darkMode === "1"}
                    onChange={(e) => handleThemeChange(e.target.checked)}
                  />
                  <Form.Label
                    htmlFor="darkmode-toggle"
                    className="ml-3 fw-bolder"
                  ></Form.Label>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      )}

      <Modal show={showModalImg} onHide={() => setShowModalImg(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{translate("Preview Image")}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex justify-content-center">
          <img
            src={pathImg || ""}
            alt="preview"
            className="img-fluid"
            style={{ maxWidth: "100%", height: "auto" }}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default Profile;
